import { DOCUMENT, isPlatformServer } from '@angular/common';
import { Component, effect, inject, PLATFORM_ID } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { Meta, Title } from '@angular/platform-browser';
import { catchError, map, of, switchMap } from 'rxjs';
import { buildPageTitle } from '../../../shared/constants/site-meta';
import type { BlogPageData } from '../../../shared/models/blog.models';
import { BlogsService } from '../../../shared/services/blogs.service';
import { BlogRendererComponent } from '../../components/blog-renderer/blog-renderer.component';
import { MarketingBlogBreadcrumbComponent } from '../../components/marketing-blog-breadcrumb.component';
import { MarketingBlogGuideDownloadComponent } from '../../components/marketing-blog-guide-download.component';
import { MarketingBlogRelatedComponent } from '../../components/marketing-blog-related.component';
import { MarketingConsultationCtaComponent } from '../../components/marketing-consultation-cta.component';
import { MarketingFooterComponent } from '../../components/marketing-footer.component';
import { MarketingHeaderComponent } from '../../components/marketing-header.component';

export type BlogDetailLoadState =
  | { kind: 'pending' }
  | { kind: 'error' }
  | { kind: 'ok'; page: BlogPageData };

@Component({
  selector: 'app-marketing-blog-detail-page',
  standalone: true,
  imports: [
    RouterLink,
    MarketingHeaderComponent,
    MarketingBlogBreadcrumbComponent,
    BlogRendererComponent,
    MarketingBlogGuideDownloadComponent,
    MarketingConsultationCtaComponent,
    MarketingBlogRelatedComponent,
    MarketingFooterComponent,
  ],
  templateUrl: './blog-detail-page.component.html',
})
export class MarketingBlogDetailPageComponent {
  private readonly route = inject(ActivatedRoute);
  private readonly blogs = inject(BlogsService);
  private readonly title = inject(Title);
  private readonly meta = inject(Meta);
  private readonly document = inject(DOCUMENT);
  private readonly platformId = inject(PLATFORM_ID);

  readonly pageState = toSignal(
    this.route.paramMap.pipe(
      switchMap((params) => {
        const slug = params.get('slug');
        if (!slug) return of({ kind: 'error' as const });
        return this.blogs.getPublicBySlug(slug).pipe(
          map((page) => ({ kind: 'ok' as const, page })),
          catchError(() =>
            isPlatformServer(this.platformId)
              ? of({ kind: 'pending' as const })
              : of({ kind: 'error' as const }),
          ),
        );
      }),
    ),
    { initialValue: { kind: 'pending' as const } satisfies BlogDetailLoadState },
  );

  constructor() {
    effect(() => {
      const s = this.pageState();
      if (s.kind === 'ok') this.applySeo(s.page);
    });
  }

  formatDate(iso?: string): string {
    if (!iso) return '';
    return new Date(iso).toLocaleDateString('en-IN', { year: 'numeric', month: 'long', day: 'numeric' });
  }

  sourcePage(slug: string): string {
    return `/blog/${slug}`;
  }

  private applySeo(page: BlogPageData): void {
    const seo = page.seo;
    this.title.setTitle(buildPageTitle(seo.title));
    this.meta.updateTag({ name: 'description', content: seo.description });
    if (seo.keywords?.length) {
      this.meta.updateTag({ name: 'keywords', content: seo.keywords.join(', ') });
    }
    this.meta.updateTag({ property: 'og:title', content: seo.title });
    this.meta.updateTag({ property: 'og:description', content: seo.description });
    this.meta.updateTag({ property: 'og:type', content: 'article' });
    if (seo.ogImage?.cdnUrl) {
      this.meta.updateTag({ property: 'og:image', content: seo.ogImage.cdnUrl });
    }
    if (seo.noindex) {
      this.meta.updateTag({ name: 'robots', content: 'noindex,nofollow' });
    } else {
      this.meta.updateTag({ name: 'robots', content: 'index,follow' });
    }
    this.injectStructuredData(page);
  }

  private injectStructuredData(page: BlogPageData): void {
    const head = this.document.head;
    head.querySelectorAll('script[data-blog-ld]').forEach((el) => el.remove());
    const payloads = [page.structuredData.article, page.structuredData.breadcrumb];
    if (page.structuredData.faq) payloads.push(page.structuredData.faq);
    for (const data of payloads) {
      const script = this.document.createElement('script');
      script.type = 'application/ld+json';
      script.setAttribute('data-blog-ld', 'true');
      script.textContent = JSON.stringify(data);
      head.appendChild(script);
    }
  }
}
